import { Box, Grid2 } from '@mui/material';
import * as Yup from 'yup';
import BackIcon from '@assets/images/BackIcon.svg';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import type { FacilityClinicianMapping } from '../../API/services/FacilityClinicianMappingService';
import CustomAutoCompleteSingleSelection from '@qler-view/shared_lib/src/shared/lib/ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import { Form, Formik } from 'formik';
import formatPhoneNumber from '../components/FormatPhoneNumber';
import CustomDatePicker from '@qler-view/shared_lib/src/shared/lib/ui-components/DateTimePicker/CustomDatePicker';
import moment from 'moment';
import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import CustomSwitch from '@qler-view/shared_lib/src/shared/lib/ui-components/Switch/CustomSwitch';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import {
  useGetFacilityClinicalMappingDetailsById,
  useGetFacilityClinicianMappingMetaData,
  useSaveOrUpdateFacilityClinicianMapping,
} from '../../API/hooks/FacilityClinicianMappingHook';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import {
  CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH,
  FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH,
} from '../../routers/routePaths';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';
import CustomSelect from '@qler-view/shared_lib/src/shared/lib/ui-components/Select/CustomSelect';
import { toValidNumber } from '../components/CommonFunction';

export const ProviderFacilityInitialValues: FacilityClinicianMapping = {
  id: 0,
  facilityId: 0,
  providerId: 0,
  status: true,
  facilityAssignedEmail: '',
  primaryPhone: '',
  username: '',
  networkAccessId: '',
  emrId: '',
  vpnGatewayId: '',
  privilegesIssuedDate: '',
  mfaConfiguration: '',
  mfa: '',
  followUpRequired: false,
  followUpContact: '',
  trainingProgress: 0,
  trainingType: 0,
  providerFacilityNotes: '',
};

type facilityDeatils = {
  facilityName?: string;
  providerName?: string;
  action: string;
  type: string;
  ids?: number;
};

const AddFacilityClinicianMapping = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { facilityId, clinicianId } = useParams<{
    facilityId?: string;
    clinicianId?: string;
  }>();

  const location = useLocation() as {
    state?: facilityDeatils;
  };

  const { facilityName, action, type, ids } = location.state || {};

  const idRef = useRef<facilityDeatils | null>(null);

  const { FacilityClinicianMappingMetaData } =
    useGetFacilityClinicianMappingMetaData(
      userId,
      toValidNumber(facilityId),
      toValidNumber(clinicianId),
      toValidNumber(ids),
      action ?? ''
    );

  useEffect(() => {
    if (location.state) {
      idRef.current = location.state;
    }
  }, [location.state]);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [
    facilityClinicianMappingSubmitAction,
    setFacilityClinicianMappingSubmitAction,
  ] = useState<string>('');

  const [
    facilityClinicianMappingInitialValues,
    setFacilityClinicianMappingInitialValues,
  ] = useState(ProviderFacilityInitialValues);

  const onSuccess = () => {
    type === 'facility'
      ? navigate(FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH + `/${facilityId}`, {
          state: {
            facilityName: facilityName,
            type: type,
            ids: facilityId,
            message:
              action === 'edit'
                ? 'Facility Clinician Mapping updated successfully!'
                : 'Facility Clinician Mapping saved successfully!',
          },
          replace: true,
        })
      : navigate(
          CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH + `/${clinicianId}`,
          {
            state: {
              providerName: facilityName,
              type: type,
              ids: clinicianId,
              message:
                action === 'edit'
                  ? 'Facility Clinician Mapping updated successfully!'
                  : 'Facility Clinician Mapping saved successfully!',
            },
            replace: true,
          }
        );
  };
  const { FacilityClinicainMappingDetailsById, isLoading } =
    useGetFacilityClinicalMappingDetailsById(userId, Number(ids), action);

  useEffect(() => {
    if (FacilityClinicainMappingDetailsById) {
      setFacilityClinicianMappingInitialValues({
        ...ProviderFacilityInitialValues,
        ...FacilityClinicainMappingDetailsById,
      });
    }
  }, [FacilityClinicainMappingDetailsById]);

  const { mutate: saveOrUpdateFacilityClinicianMapping } =
    useSaveOrUpdateFacilityClinicianMapping(onSuccess);

  const handleFacilityClinicianMappingSaveOrUpdate = async (
    facilityClinicianMappingData: typeof facilityClinicianMappingInitialValues
  ) => {
    const payload = {
      ...facilityClinicianMappingData,

      ...(facilityId && { facilityId: Number(facilityId) }),
      ...(clinicianId && { providerId: Number(clinicianId) }),
    };

    if (facilityClinicianMappingSubmitAction) {
      saveOrUpdateFacilityClinicianMapping({
        userId,
        action: facilityClinicianMappingSubmitAction,
        ...payload,
      });
    }
  };

  const handleOpenConfirmDialog = (state: boolean) => {
    setOpenConfirmDialog(state);
  };

  const facilityClinicianMappingValidationSchema = Yup.object({
    providerId: Yup.number()
      .nullable()
      .when([], {
        is: () => !!facilityId && !clinicianId,
        then: (schema) =>
          schema
            .required('Provider Name is required')
            .min(1, 'Provider Name is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    facilityId: Yup.number()
      .nullable()
      .when([], {
        is: () => !!clinicianId && !facilityId,
        then: (schema) =>
          schema
            .required('Facility Name is required')
            .min(1, 'Facility Name is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    primaryPhone: Yup.string()
      .nullable()
      .test(
        'valid-primaryPhone',
        'Phone must be in format (123) 456-7890',
        (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
      ),
    facilityAssignedEmail: Yup.string().nullable().email('Invalid email'),

    privilegesIssuedDate: Yup.string().required(
      'Privileges Issued Date is required'
    ),
    providerFacilityNotes: Yup.string().max(
      1000,
      'Notes must be no more than 1000 characters long.'
    ),
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button"
                icon={<BackIcon />}
                onClick={() => {
                  if (type === 'facility') {
                    navigate(
                      FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH +
                        `/${facilityId}`,
                      {
                        state: {
                          facilityName: facilityName,
                          type: type,
                          ids: facilityId,
                        },
                      }
                    );
                  } else if (action === 'edit') {
                    navigate(
                      CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH +
                        `/${FacilityClinicainMappingDetailsById?.providerId}`,
                      {
                        state: {
                          providerName: facilityName,
                          type: type,
                          ids: facilityId,
                        },
                      }
                    );
                  } else {
                    navigate(
                      CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH +
                        `/${clinicianId}`,
                      {
                        state: { providerName: facilityName, type: type },
                        replace: true,
                      }
                    );
                  }
                }}
              ></CustomIconButton>

              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-facility-text"
              >
                {action === 'edit'
                  ? 'Edit Clinician To Facility'
                  : 'Add Clinician To Facility'}
              </CustomTypography>
              <CustomTypography
                className="pl-12"
                variant="h4"
                id="partner-business-id"
              >
                {facilityId ? `(${facilityName})` : ''}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Formik
        initialValues={facilityClinicianMappingInitialValues}
        validateOnMount={false}
        validationSchema={facilityClinicianMappingValidationSchema}
        onSubmit={(values) => {
          handleFacilityClinicianMappingSaveOrUpdate(values);
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          // isValid,
          // dirty,
          handleSubmit,
          //   handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <Box className="form-background p-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="provider-facility-info-text"
                  >
                    Provider Facility Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  {type === 'facility' && (
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="provider-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Provider Name <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomAutoCompleteSingleSelection
                        id="provider-name"
                        placeholder="Select Provider"
                        size="small"
                        options={
                          FacilityClinicianMappingMetaData?.activeClinicians ??
                          []
                        }
                        value={
                          FacilityClinicianMappingMetaData?.activeClinicians?.find(
                            (x) => x.id === values.providerId
                          ) ?? null
                        }
                        onChange={(selectedOptions) => {
                          const selectedId = selectedOptions
                            ? selectedOptions.id
                            : null;
                          setFieldValue('providerId', selectedId);
                        }}
                        noOptionsText="No Provider Found"
                      />
                      {errors?.providerId && touched?.providerId && (
                        <CustomTypography
                          id="validate-provider"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.providerId}
                        </CustomTypography>
                      )}
                    </Grid2>
                  )}
                  {type === 'clinician' && (
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Facility Name <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomAutoCompleteSingleSelection
                        id="facility-name"
                        placeholder="Select Facility"
                        size="small"
                        options={
                          FacilityClinicianMappingMetaData?.facilityList ?? []
                        }
                        value={
                          FacilityClinicianMappingMetaData?.facilityList?.find(
                            (x) => x.id === values.facilityId
                          ) ?? null
                        }
                        onChange={(selectedOptions) => {
                          const facilityId = selectedOptions
                            ? selectedOptions.id
                            : null;
                          setFieldValue('facilityId', facilityId);
                        }}
                        noOptionsText="No Facility Found"
                      />
                      {errors?.facilityId && touched?.facilityId && (
                        <CustomTypography
                          id="validate-facility"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.facilityId}
                        </CustomTypography>
                      )}
                    </Grid2>
                  )}
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-assigned-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Facility Assigned Email
                    </CustomTypography>
                    <CustomTextField
                      id="txt-facility-assigned-email"
                      placeholder="Type Facility Assigned Email"
                      size="small"
                      value={values.facilityAssignedEmail}
                      onChange={(event) => {
                        setFieldValue(
                          'facilityAssignedEmail',
                          event.target.value
                        );
                      }}
                    />
                    {errors?.facilityAssignedEmail &&
                      touched?.facilityAssignedEmail && (
                        <CustomTypography
                          id="validate-facility-assigned-email"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.facilityAssignedEmail}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-primary-phone-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Primary Phone
                    </CustomTypography>
                    <CustomTextField
                      id="txt-facility-clinician-mapping-primary-phone"
                      placeholder="Type Primary Phone"
                      size="small"
                      value={values.primaryPhone}
                      onChange={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue('primaryPhone', formatted);
                      }}
                      onBlur={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue('primaryPhone', formatted);
                      }}
                    />
                    {errors?.primaryPhone && (
                      <CustomTypography
                        id="validate-facility-clinician-mapping-primary-phone"
                        color="error"
                        variant="body3"
                        className="pt-8 text-align-left"
                      >
                        {errors.primaryPhone}
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-clinician-mapping-username-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Username
                    </CustomTypography>
                    <CustomTextField
                      id="txt-facility-clinician-mapping-username"
                      placeholder="Type QLER View Username"
                      size="small"
                      value={values.username}
                      onChange={(event) => {
                        setFieldValue('username', event.target.value);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="network-access-id-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Network Access ID
                    </CustomTypography>
                    <CustomTextField
                      id="txt-network-access-id"
                      placeholder="Type Network Access ID"
                      size="small"
                      value={values.networkAccessId}
                      onChange={(event) => {
                        setFieldValue('networkAccessId', event.target.value);
                      }}
                      onBlur={(event) => {
                        setFieldValue('networkAccessId', event.target.value);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="emr-id-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      EMR ID
                    </CustomTypography>
                    <CustomTextField
                      id="txt-emr-id"
                      placeholder="Type EMR ID"
                      size="small"
                      value={values.emrId}
                      onChange={(event) => {
                        setFieldValue('emrId', event.target.value);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="vpn-gateway-id-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      VPN Gateway ID
                    </CustomTypography>
                    <CustomTextField
                      id="txt-vpn-gateway-id"
                      placeholder="Type VPN Gateway ID"
                      size="small"
                      value={values.vpnGatewayId}
                      onChange={(event) => {
                        setFieldValue('vpnGatewayId', event.target.value);
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="privileges-issued-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Privileges Issued <span className="color-red">*</span>
                    </CustomTypography>
                    <CustomDatePicker
                      id="privileges-issued-date-picker"
                      format="MM-DD-YYYY"
                      value={moment(values.privilegesIssuedDate)}
                      onChange={(date) => {
                        setFieldValue(
                          'privilegesIssuedDate',
                          date ? date.format('MM-DD-YYYY') : ''
                        );
                      }}
                    />
                    {errors?.privilegesIssuedDate &&
                      touched?.privilegesIssuedDate && (
                        <CustomTypography
                          id="validate-privileges-issued-date"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.privilegesIssuedDate}
                        </CustomTypography>
                      )}
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="mfa-configuration-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      MFA Configuration
                    </CustomTypography>
                    <CustomTextField
                      id="txt-mfa-configuration"
                      placeholder="Type MFA Configuration"
                      size="small"
                      value={values.mfaConfiguration}
                      onChange={(event) => {
                        setFieldValue('mfaConfiguration', event.target.value);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="mfa-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      MFA
                    </CustomTypography>
                    <CustomTextField
                      id="txt-mfa"
                      placeholder="Type MFA"
                      size="small"
                      value={values.mfa}
                      onChange={(event) => {
                        setFieldValue('mfa', event.target.value);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-clinician-mapping-status-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Status
                    </CustomTypography>
                    <Box className="text-align-left">
                      <CustomSwitch
                        id="facility-clinician-mapping-status-switch"
                        checked={values.status}
                        onChange={(event) => {
                          setFieldValue('status', event.target.checked);
                        }}
                        labelPlacement="end"
                        label={values.status ? 'Active' : 'Inactive'}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="provider-setup-title"
                    >
                      Provider Setup
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="follow-up-contact-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Follow-Up Contact
                    </CustomTypography>
                    <CustomTextField
                      id="txt-follow-up-contact"
                      placeholder="Type Follow-Up Contact"
                      size="small"
                      value={values.followUpContact}
                      onChange={(event) => {
                        setFieldValue('followUpContact', event.target.value);
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="training-progress-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Training Progress
                    </CustomTypography>
                    <CustomSelect
                      id="training-progress-select"
                      placeholder="Select Training Progress"
                      size="small"
                      onChange={(value) => {
                        setFieldValue('trainingProgress', Number(value));
                      }}
                      selectedValue={values.trainingProgress}
                      options={
                        FacilityClinicianMappingMetaData?.trainingProgressList ??
                        []
                      }
                      fullWidth
                    ></CustomSelect>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="training-type-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Training Type
                    </CustomTypography>
                    <CustomSelect
                      id="training-type-select"
                      placeholder="Select Training Type"
                      size="small"
                      onChange={(value) => {
                        setFieldValue('trainingType', Number(value));
                      }}
                      selectedValue={values.trainingType}
                      options={
                        FacilityClinicianMappingMetaData?.trainingTypeList ?? []
                      }
                      fullWidth
                    ></CustomSelect>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="follow-up-required-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Follow-Up Required?
                    </CustomTypography>
                    <Box className="text-align-left">
                      <CustomSwitch
                        id="follow-up-switch"
                        checked={values.followUpRequired}
                        onChange={(event) => {
                          setFieldValue(
                            'followUpRequired',
                            event.target.checked
                          );
                        }}
                        labelPlacement="end"
                        label={values.followUpRequired ? 'Yes' : 'No'}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="provider-facility-text"
                      >
                        Provider Facility Notes
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4}>
                    <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                      <CustomTypography
                        id="provider-facility-notes-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Notes
                      </CustomTypography>
                      <CustomTextField
                        id="txt-notes"
                        placeholder="Type Notes"
                        size="small"
                        multiLine
                        rows={4}
                        value={values.providerFacilityNotes}
                        onChange={(event) => {
                          setFieldValue(
                            'providerFacilityNotes',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                      {errors.providerFacilityNotes && (
                        <CustomTypography
                          id="validate-provider-facility-notes"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.providerFacilityNotes}
                        </CustomTypography>
                      )}
                    </Grid2>
                  </Grid2>
                </Box>
                <Box className="pt-32">
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <Box className="display-flex justify-end">
                        <Box className="pr-12">
                          <CustomButton
                            id="cancel-button"
                            onClick={() => {
                              handleOpenConfirmDialog(true);
                            }}
                            variant="outlined"
                          >
                            Cancel
                          </CustomButton>
                        </Box>
                        <Box>
                          <CustomButton
                            id="save-button"
                            onClick={() => {
                              setFacilityClinicianMappingSubmitAction(
                                action === 'edit' ? 'Update' : 'Save'
                              );
                              handleSubmit();
                            }}
                            variant="contained"
                            // disabled={!isValid || !dirty}
                          >
                            {action === 'edit' ? 'Update' : 'Save'}
                          </CustomButton>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
      <Box>
        <ConfirmDiscardDialog
          open={openConfirmDialog}
          onClose={() => {
            handleOpenConfirmDialog(false);
          }}
          navigateTo={
            type === 'facility'
              ? FACILITY_CLINICAIN_MAPPED_LIST_ROUTE_PATH + `/${facilityId}`
              : CLINICAIN_FACILITY_MAPPED_LIST_ROUTE_PATH +
                `/${FacilityClinicainMappingDetailsById?.providerId}`
          }
          navigationState={{ facilityName }}
          type={type}
          id={Number(facilityId)}
        />
      </Box>
    </Box>
  );
};

export default AddFacilityClinicianMapping;
